import * as React from 'react';
import * as SelectPrimitive from '@radix-ui/react-select';
import { CheckIcon, ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import { cn } from '@noor/lib/cn';

function Select(props: React.ComponentProps<typeof SelectPrimitive.Root>) {
  return <SelectPrimitive.Root data-slot="select" {...props} />;
}

function SelectGroup(props: React.ComponentProps<typeof SelectPrimitive.Group>) {
  return <SelectPrimitive.Group data-slot="select-group" {...props} />;
}

function SelectValue(props: React.ComponentProps<typeof SelectPrimitive.Value>) {
  return <SelectPrimitive.Value data-slot="select-value" {...props} />;
}

function SelectTrigger({
  className,
  size = 'default',
  children,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Trigger> & {
  size?: 'sm' | 'default';
}) {
  return (
    <SelectPrimitive.Trigger
      data-slot="select-trigger"
      data-size={size}
      className={cn(
        // Match the Input: 40px tall, 1px #e5e7eb hairline, field shadow,
        // 14px regular black text, 14px regular #9ca3af placeholder, -0.28px
        // tracking, 20px chevron. Dimensions resolve through the shared
        // `--field-*` sizing handles (component-sizing.css).
        "flex w-fit items-center justify-between gap-2 rounded-[var(--field-radius)] border border-input-border bg-transparent px-[var(--field-padding-x)] py-2 text-[length:var(--field-font-size)] font-[var(--field-font-weight)] tracking-[var(--field-tracking)] whitespace-nowrap text-input-fg shadow-field transition-[color,box-shadow] outline-none focus-visible:border-input-border-focus focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:cursor-not-allowed disabled:border-form-control-border-disabled disabled:bg-form-control-bg-disabled disabled:text-form-control-fg-disabled aria-invalid:border-input-border-invalid aria-invalid:ring-input-border-invalid/20 data-[placeholder]:text-[length:var(--field-font-size)] data-[placeholder]:font-[var(--field-font-weight)] data-[placeholder]:tracking-[var(--field-tracking)] data-[placeholder]:text-input-placeholder data-[size=default]:h-[var(--field-height)] data-[size=sm]:h-[var(--field-height-sm)] *:data-[slot=select-value]:line-clamp-1 *:data-[slot=select-value]:flex *:data-[slot=select-value]:items-center *:data-[slot=select-value]:gap-2 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5 [&_svg:not([class*='text-'])]:text-muted-foreground",
        className,
      )}
      {...props}
    >
      {children}
      <SelectPrimitive.Icon asChild>
        <ChevronDownIcon className="size-5 text-muted-foreground" />
      </SelectPrimitive.Icon>
    </SelectPrimitive.Trigger>
  );
}

function SelectContent({
  className,
  children,
  position = 'popper',
  viewportProps,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Content> & {
  viewportProps?: React.ComponentProps<typeof SelectPrimitive.Viewport>;
}) {
  return (
    <SelectPrimitive.Portal>
      <SelectPrimitive.Content
        data-slot="select-content"
        className={cn(
          // Deliberately the SAME surface as `ActionMenuContent` — the 3-dot
          // popup on a product/category card — so every popup in the panel is
          // one shape: 16px radius, 4px padding, soft ambient shadow, no
          // border. A select that opened a subtly different card read as a
          // different control kit on the same screen.
          'z-50 max-h-(--radix-select-content-available-height) w-[var(--radix-select-trigger-width)] min-w-0 origin-(--radix-select-content-transform-origin) overflow-x-hidden overflow-y-auto rounded-2xl bg-select-content-bg p-1 text-select-content-fg shadow-lg data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
          position === 'popper' &&
            'data-[side=bottom]:translate-y-1.5 data-[side=left]:-translate-x-1 data-[side=right]:translate-x-1 data-[side=top]:-translate-y-1.5',
          className,
        )}
        position={position}
        {...props}
      >
        <SelectScrollUpButton />
        <SelectPrimitive.Viewport
          {...viewportProps}
          className={cn(
            position === 'popper' &&
              'h-[var(--radix-select-trigger-height)] w-full scroll-my-1',
            viewportProps?.className,
          )}
        >
          {children}
        </SelectPrimitive.Viewport>
        <SelectScrollDownButton />
      </SelectPrimitive.Content>
    </SelectPrimitive.Portal>
  );
}

function SelectLabel({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Label>) {
  return (
    <SelectPrimitive.Label
      data-slot="select-label"
      className={cn('px-2 py-1.5 text-xs text-muted-foreground', className)}
      {...props}
    />
  );
}

function SelectItem({
  className,
  children,
  icon,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.Item> & {
  /**
   * A leading glyph, rendered OUTSIDE `ItemText`.
   *
   * That placement is the whole point: Radix mirrors `ItemText`'s children
   * into the closed trigger, so an icon passed as part of `children` is drawn
   * twice on a trigger that renders its own — once by the mirror, once by the
   * caller — and pushes the label onto a second line. Kept out of the mirror,
   * the trigger decides for itself whether to show a glyph.
   */
  icon?: React.ReactNode;
}) {
  return (
    <SelectPrimitive.Item
      data-slot="select-item"
      className={cn(
        // Matches `ActionMenuItem` — 12px radius, 12px padding, 14px medium
        // tracking-tight, 20px glyphs — with the extra right padding the
        // selected-check indicator needs.
        "relative flex w-full cursor-pointer items-center gap-2 rounded-xl p-3 pr-8 text-sm font-medium tracking-tight outline-hidden transition-colors select-none focus:bg-select-item-bg-active focus:text-select-item-fg-active data-[disabled]:pointer-events-none data-[disabled]:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0 [&_svg:not([class*='size-'])]:size-5 [&_svg:not([class*='text-'])]:text-muted-foreground *:[span]:last:flex *:[span]:last:items-center *:[span]:last:gap-2",
        className,
      )}
      {...props}
    >
      <span className="absolute right-2 flex size-3.5 items-center justify-center">
        <SelectPrimitive.ItemIndicator>
          <CheckIcon className="size-4" />
        </SelectPrimitive.ItemIndicator>
      </span>
      {icon}
      <SelectPrimitive.ItemText>{children}</SelectPrimitive.ItemText>
    </SelectPrimitive.Item>
  );
}

function SelectScrollUpButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollUpButton>) {
  return (
    <SelectPrimitive.ScrollUpButton
      data-slot="select-scroll-up-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronUpIcon className="size-4" />
    </SelectPrimitive.ScrollUpButton>
  );
}

function SelectScrollDownButton({
  className,
  ...props
}: React.ComponentProps<typeof SelectPrimitive.ScrollDownButton>) {
  return (
    <SelectPrimitive.ScrollDownButton
      data-slot="select-scroll-down-button"
      className={cn('flex cursor-default items-center justify-center py-1', className)}
      {...props}
    >
      <ChevronDownIcon className="size-4" />
    </SelectPrimitive.ScrollDownButton>
  );
}

export {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectScrollDownButton,
  SelectScrollUpButton,
  SelectTrigger,
  SelectValue,
};
